import React from 'react';
import { flatMap, get } from 'lodash';
import { Box, Text } from '@myob/myob-widgets';
import { ProductData } from 'hooks/useGetProduct/types';
import isNZProduct from 'utils/IsNZProduct';
import { formatToTwoDecimalPlaces, getProductTotalPrice } from 'utils/pricingUtils';
import style from './PlanTotal.common.module.scss';

export type PlanTotalProps = {
  productData: ProductData;
  addOnProductsData?: ProductData[];
};

export const PlanTotal: React.FC<PlanTotalProps> = ({ productData, addOnProductsData = [] }) => {
  const productItems = get(productData, 'items', []);
  const productItem = productItems[0];

  if (!productItem) {
    return null;
  }

  const gstLabel = isNZProduct(productItem.productId) ? '(+ GST)' : '(inc. GST)';
  const addOnProductItems = flatMap(addOnProductsData, (addOnProduct) => addOnProduct.items ?? []);
  const totalPrice = formatToTwoDecimalPlaces(getProductTotalPrice([...productItems, ...addOnProductItems]));

  return (
    <Box
      className={style.planTotal}
      data-testid='plan-total'
      display='flex'
      flexDirection='column'
    >
      <Box
        className={style.totalRow}
        display='flex'
        justifyContent='space-between'
        alignItems='flex-start'
      >
        <Box className={style.totalLabel} display='flex'>
          <Text
            className={style.totalLabelStrong}
            as='span'
            marginBottom='clear'
            tone='neutralStrongest'
          >
            Total
          </Text>
          <Text
            className={style.totalLabelGst}
            as='span'
            marginBottom='clear'
            tone='neutralStrongest'
          >
            {gstLabel}
          </Text>
        </Box>
        <Box
          className={style.totalPrice}
          display='flex'
          alignItems='end'
        >
          <Text
            className={style.priceAmount}
            as='span'
            marginBottom='clear'
            tone='neutralStrongest'
          >
            ${totalPrice}
          </Text>
          <Text
            className={style.priceSchedule}
            as='span'
            marginBottom='clear'
            tone='neutralStrongest'
          >
            /month
          </Text>
        </Box>
      </Box>
      {addOnProductsData.length > 0 && (
        <Text
          className={style.commitment}
          as='p'
          data-testid='plan-total-commitment'
          marginBottom='clear'
          tone='neutralStrongest'
        >
          12 month commitment
        </Text>
      )}
    </Box>
  );
};
